<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { format, fromUnixTime } from 'date-fns';
import ReportMetricCard from './ReportMetricCard.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ReportsAPI from 'dashboard/api/reports';
import { formatTime } from '@chatwoot/utils';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const { t } = useI18n();

const conversationCount = ref('0');
const outgoingCount = ref('0');
const messageCount = ref('0');
const copilotMessageCount = ref('0');
const successRate = ref('--');
const resolveTime = ref('--');
const isLoading = ref(false);
const groupedData = ref([]);
const selectedGroupIndex = ref(null);

const hasGroupedData = computed(
  () => props.filters.groupBy && groupedData.value.length > 0
);

const isFiltered = computed(() => selectedGroupIndex.value !== null);

const displayedGroupedData = computed(() => {
  if (!isFiltered.value) return groupedData.value;
  const row = groupedData.value[selectedGroupIndex.value];
  if (!row) return groupedData.value;
  return [row];
});

const pct = (val, base) => (base ? Math.round((val / base) * 10000) / 100 : 0);

const tableData = computed(() => {
  const rows = displayedGroupedData.value;
  if (!isFiltered.value) return rows;

  return rows.map(row => {
    const base = row.total || 1;
    return {
      ...row,
      total_pct: 100,
      bot_responses_pct: pct(row.bot_responses, base),
      human_copilot_pct: pct(row.human_copilot, base),
      copilot_not_used_pct: pct(row.copilot_not_used, base),
    };
  });
});

const firstColumnLabel = computed(() => {
  return props.filters.groupBy === 'labels'
    ? t('BOT_REPORTS.GROUPED.TABLE.LABEL')
    : t('BOT_REPORTS.GROUPED.TABLE.INBOX');
});

const CHART_COLORS = [
  '#1F93FF',
  '#34D399',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#6366F1',
  '#84CC16',
  '#06B6D4',
  '#D946EF',
  '#78716C',
  '#0EA5E9',
  '#A3E635',
];

const pieChartData = computed(() => {
  if (isFiltered.value) {
    const row = groupedData.value[selectedGroupIndex.value];
    if (!row) return { labels: [], datasets: [{ data: [] }] };
    return {
      labels: [
        t('BOT_REPORTS.GROUPED.TABLE.BOT_RESPONSES'),
        t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_USED'),
        t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_NOT_USED'),
      ],
      datasets: [
        {
          data: [row.bot_responses, row.human_copilot, row.copilot_not_used],
          backgroundColor: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[3]],
        },
      ],
    };
  }
  const data = groupedData.value;
  return {
    labels: data.map(d => d.name),
    datasets: [
      {
        data: data.map(d => d.total),
        backgroundColor: data.map(
          (_, i) => CHART_COLORS[i % CHART_COLORS.length]
        ),
      },
    ],
  };
});

const onChartClick = (event, elements) => {
  if (!elements.length) {
    selectedGroupIndex.value = null;
    return;
  }
  const clickedIndex = elements[0].index;
  selectedGroupIndex.value =
    selectedGroupIndex.value === clickedIndex ? null : clickedIndex;
};

const outlabelsPlugin = {
  id: 'outlabels',
  afterDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data.length) return;

    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const total = dataset.data.reduce((a, b) => a + b, 0);
    if (!total) return;

    meta.data.forEach((arc, i) => {
      const { x, y, startAngle, endAngle, outerRadius } = arc.getProps([
        'x',
        'y',
        'startAngle',
        'endAngle',
        'outerRadius',
      ]);
      const midAngle = (startAngle + endAngle) / 2;

      // Point on the edge of the arc
      const edgeX = x + Math.cos(midAngle) * outerRadius;
      const edgeY = y + Math.sin(midAngle) * outerRadius;

      // Point further out for the elbow
      const elbowLen = 20;
      const elbowX = x + Math.cos(midAngle) * (outerRadius + elbowLen);
      const elbowY = y + Math.sin(midAngle) * (outerRadius + elbowLen);

      // Horizontal line direction
      const isRight = midAngle < Math.PI / 2 || midAngle > 1.5 * Math.PI;
      const lineLen = 30;
      const endX = elbowX + (isRight ? lineLen : -lineLen);

      // Draw connector line
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(edgeX, edgeY);
      ctx.lineTo(elbowX, elbowY);
      ctx.lineTo(endX, elbowY);
      ctx.strokeStyle = dataset.backgroundColor[i] || '#999';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Draw label text
      const value = dataset.data[i];
      const percent = ((value / total) * 100).toFixed(1);
      const label = chart.data.labels[i];
      const text = `${label}: ${value.toLocaleString()} (${percent}%)`;

      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.fillStyle = '#374151';
      ctx.textAlign = isRight ? 'left' : 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, endX + (isRight ? 4 : -4), elbowY);
      ctx.restore();
    });
  },
};

const pieChartPlugins = [outlabelsPlugin];

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 80,
  },
  onClick: onChartClick,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: ctx => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const percent = total ? ((ctx.raw / total) * 100).toFixed(1) : 0;
          return `${ctx.label}: ${ctx.raw.toLocaleString()} (${percent}%)`;
        },
      },
    },
    datalabels: {
      display: false,
    },
  },
};

const formatPct = value => {
  return value.toFixed(1).replace('.', ',');
};

const formatToPercent = value => {
  if (value == null) return '--';
  return `${(value * 100).toFixed(0)}%`;
};

const ratingPeriod = computed(() => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const since = props.filters.from
    ? format(fromUnixTime(props.filters.from), 'yyyy-MM-dd')
    : undefined;
  const until = props.filters.to
    ? format(fromUnixTime(props.filters.to), 'yyyy-MM-dd')
    : undefined;

  // An upper bound of today (or later) is redundant since no conversations exist
  // in the future, so we drop it to keep the resulting filter clean.
  return { since, until: until && until < today ? until : undefined };
});

// Route to the conversations list filtered by the clicked thumb rating, scoped
// to this row's inbox/label grouping and the selected report period. ChatList.vue
// turns these query params into a real, editable conversation filter.
const ratingLink = (row, rating) => ({
  name: 'home',
  query: {
    conversation_rating: rating,
    rating_group_by: props.filters.groupBy,
    rating_group_value: props.filters.groupBy === 'inboxes' ? row.id : row.name,
    rating_since: ratingPeriod.value.since,
    rating_until: ratingPeriod.value.until,
  },
});

const fetchMetrics = () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }
  isLoading.value = true;
  ReportsAPI.getBotMetrics(props.filters)
    .then(response => {
      conversationCount.value =
        response.data.bot_conversation_count.toLocaleString();
      outgoingCount.value =
        response.data.outgoing_message_count.toLocaleString();
      messageCount.value = response.data.bot_message_count.toLocaleString();
      copilotMessageCount.value =
        response.data.bot_copilot_message_count.toLocaleString();
      successRate.value = formatToPercent(response.data.bot_success_rate);
      resolveTime.value = response.data.bot_resolve_time
        ? formatTime(response.data.bot_resolve_time)
        : '--';
      groupedData.value = response.data.grouped || [];
      selectedGroupIndex.value = null;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

watch(() => props.filters, fetchMetrics, { deep: true });

onMounted(fetchMetrics);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex flex-wrap mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.CONVERSATION_COUNT.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.CONVERSATION_COUNT.TOOLTIP')"
        :value="conversationCount"
        :is-loading="isLoading"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.OUTGOING_COUNT.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.OUTGOING_COUNT.TOOLTIP')"
        :value="outgoingCount"
        :is-loading="isLoading"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.MESSAGE_COUNT.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.MESSAGE_COUNT.TOOLTIP')"
        :value="messageCount"
        :is-loading="isLoading"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.COPILOT_COUNT.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.COPILOT_COUNT.TOOLTIP')"
        :value="copilotMessageCount"
        :is-loading="isLoading"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.SUCCESS_RATE.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.SUCCESS_RATE.TOOLTIP')"
        :value="successRate"
        :is-loading="isLoading"
        class="flex-1"
      />
      <ReportMetricCard
        :label="$t('BOT_REPORTS.METRIC.RESOLVE_TIME.LABEL')"
        :info-text="$t('BOT_REPORTS.METRIC.RESOLVE_TIME.TOOLTIP')"
        :value="resolveTime"
        :is-loading="isLoading"
        class="flex-1"
      />
    </div>

    <!-- Pie Chart -->
    <div
      v-if="hasGroupedData"
      class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <div class="flex items-center gap-2 mb-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{
            isFiltered
              ? groupedData[selectedGroupIndex].name
              : $t('BOT_REPORTS.GROUPED.CHART_TITLE')
          }}
        </h3>
        <button
          v-if="isFiltered"
          class="text-xs text-woot-500 hover:text-woot-600 cursor-pointer"
          @click="selectedGroupIndex = null"
        >
          {{ `\u2190 ${$t('BOT_REPORTS.GROUPED.BACK_TO_ALL')}` }}
        </button>
      </div>
      <div class="h-[400px]">
        <Doughnut
          :data="pieChartData"
          :options="pieChartOptions"
          :plugins="pieChartPlugins"
        />
      </div>
    </div>

    <!-- Table -->
    <div
      v-if="hasGroupedData"
      class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak bg-n-alpha-1">
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ firstColumnLabel }}
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                {{ $t('BOT_REPORTS.GROUPED.TABLE.CONVERSATIONS') }}
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                <span
                  v-tooltip="$t('BOT_REPORTS.GROUPED.TABLE.MESSAGES_TOOLTIP')"
                >
                  {{ $t('BOT_REPORTS.GROUPED.TABLE.MESSAGES') }}
                  <fluent-icon icon="info" size="12" class="inline ml-0.5" />
                </span>
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                <span
                  v-tooltip="$t('BOT_REPORTS.GROUPED.TABLE.PERCENTAGE_TOOLTIP')"
                >
                  {{ $t('BOT_REPORTS.GROUPED.TABLE.PERCENTAGE') }}
                  <fluent-icon icon="info" size="12" class="inline ml-0.5" />
                </span>
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                <span
                  v-tooltip="
                    $t('BOT_REPORTS.GROUPED.TABLE.AI_MESSAGES_TOOLTIP')
                  "
                >
                  {{ $t('BOT_REPORTS.GROUPED.TABLE.AI_MESSAGES') }}
                  <fluent-icon icon="info" size="12" class="inline ml-0.5" />
                </span>
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                {{ $t('BOT_REPORTS.GROUPED.TABLE.PREPARED') }}
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                <span
                  v-tooltip="
                    $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_USED_TOOLTIP')
                  "
                >
                  {{ $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_USED') }}
                  <fluent-icon icon="info" size="12" class="inline ml-0.5" />
                </span>
              </th>
              <th
                class="px-4 py-3 text-right font-medium text-n-slate-11 whitespace-nowrap"
              >
                <span
                  v-tooltip="
                    $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_NOT_USED_TOOLTIP')
                  "
                >
                  {{ $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_NOT_USED') }}
                  <fluent-icon icon="info" size="12" class="inline ml-0.5" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in tableData"
              :key="row.name"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
            >
              <td class="px-4 py-3 text-n-slate-12">
                {{ row.name }}
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ row.conversations_count.toLocaleString() }}
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ row.total.toLocaleString() }}
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12 font-medium">
                {{ formatPct(row.total_pct) }}%
              </td>
              <td
                class="px-4 py-3 text-right text-n-slate-12 whitespace-nowrap"
              >
                <span class="inline-flex items-center gap-1.5">
                  {{ row.bot_responses.toLocaleString() }}
                  <span class="inline-flex items-center gap-1 text-n-slate-11">
                    <span>(</span>
                    <router-link
                      v-tooltip="
                        $t('BOT_REPORTS.GROUPED.TABLE.THUMBS_UP_TOOLTIP')
                      "
                      :to="ratingLink(row, 'up')"
                      class="inline-flex items-center gap-0.5 text-n-teal-11 hover:underline cursor-pointer"
                    >
                      <Icon icon="i-lucide-thumbs-up" class="size-3.5" />
                      {{ (row.thumbs_up || 0).toLocaleString() }}
                    </router-link>
                    <span class="text-n-slate-8">|</span>
                    <router-link
                      v-tooltip="
                        $t('BOT_REPORTS.GROUPED.TABLE.THUMBS_DOWN_TOOLTIP')
                      "
                      :to="ratingLink(row, 'down')"
                      class="inline-flex items-center gap-0.5 text-n-ruby-11 hover:underline cursor-pointer"
                    >
                      <Icon icon="i-lucide-thumbs-down" class="size-3.5" />
                      {{ (row.thumbs_down || 0).toLocaleString() }}
                    </router-link>
                    <span>)</span>
                  </span>
                </span>
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ row.copilot_count.toLocaleString() }}
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ formatPct(row.human_copilot_pct) }}%
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ formatPct(row.copilot_not_used_pct) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-4 py-3 border-t border-n-weak bg-n-alpha-1">
        <p class="text-xs font-medium text-n-slate-11 mb-1">
          {{ $t('BOT_REPORTS.GROUPED.NOTES.TITLE') }}
        </p>
        <ul class="text-xs text-n-slate-10 space-y-0.5 list-disc pl-4">
          <li>
            <strong>{{ $t('BOT_REPORTS.GROUPED.TABLE.CONVERSATIONS') }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.CONVERSATIONS') }}
          </li>
          <li>
            <strong>{{ $t('BOT_REPORTS.GROUPED.TABLE.MESSAGES') }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.MESSAGES') }}
          </li>
          <li>
            <strong>{{ $t('BOT_REPORTS.GROUPED.TABLE.PERCENTAGE') }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.PERCENTAGE') }}
          </li>
          <li>
            <strong>{{ $t('BOT_REPORTS.GROUPED.TABLE.AI_MESSAGES') }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.AI_MESSAGES') }}
          </li>
          <li>
            <strong>{{ $t('BOT_REPORTS.GROUPED.TABLE.PREPARED') }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.PREPARED') }}
          </li>
          <li>
            <strong>{{
              $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_USED')
            }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.SUGGESTION_USED') }}
          </li>
          <li>
            <strong>{{
              $t('BOT_REPORTS.GROUPED.TABLE.SUGGESTION_NOT_USED')
            }}</strong>
            — {{ $t('BOT_REPORTS.GROUPED.NOTES.SUGGESTION_NOT_USED') }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
